import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  input,
  Input,
  linkedSignal,
  OnInit,
  Output,
  inject,
  signal,
} from "@angular/core";
import { BrandAsset, MediaType } from "@front/m19-api-client";
import { SegmentConfigType } from "@front/m19-models";
import { IButtonDirective, ITooltipDirective, ModalService, IIconComponent } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Subject, take } from "rxjs";
import {
  MediaSelectionModalComponent,
  MediaSelectionModalData,
  MediaSelectionModalResult,
} from "@m19-board/shared/media/media-selection-modal/media-selection-modal.component";
import {
  MediaPreviewModalComponent,
  MediaPreviewModalData,
} from "@m19-board/shared/media/media-preview-modal/media-preview-modal.component";

@UntilDestroy()
@Component({
  selector: "app-media-input",
  templateUrl: "./media-input.component.html",
  imports: [IButtonDirective, ITooltipDirective, TranslocoDirective, IIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaInputComponent implements OnInit {
  private modalService = inject(ModalService);
  private translocoService = inject(TranslocoService);

  @Input() testId?: string;
  disabled = input<boolean>(false);

  mediaType = input.required<MediaType>();
  medias = input.required<BrandAsset[]>();
  savedMedia = input<BrandAsset>();

  _savedMedia = linkedSignal({
    source: () => this.savedMedia(),
    computation: (source) => source,
  });

  mediaSrc: string = "";

  @Output() mediaSaved = new EventEmitter<BrandAsset>();
  @Input() removable: boolean = false;
  @Input() resetFile: Subject<void> = new Subject<void>();

  @Output() mediaDeleted = new EventEmitter<void>();

  @Output() toUpload = new EventEmitter<File>();

  newFile = signal<File | undefined>(undefined);

  ngOnInit(): void {
    this.resetFile.pipe(untilDestroyed(this)).subscribe(() => this.newFile.set(undefined));
  }

  openMediaSelectionModal() {
    this.modalService
      .openModal<MediaSelectionModalData, MediaSelectionModalResult<BrandAsset>>(MediaSelectionModalComponent, {
        modalTitle: this.translocoService.translate("media-input.select_a_file"),
        data: {
          mediaType: this.mediaType(),
          medias: this.medias(),
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((selectedMedia) => {
        if (selectedMedia.newFile) {
          this.newFile.set(selectedMedia.newFile);
          this.toUpload.emit(selectedMedia.newFile);
          this.mediaSrc = selectedMedia.mediaSrc!;
        } else {
          this.mediaSaved.emit(selectedMedia.selectedMedia);
          this._savedMedia.set(selectedMedia.selectedMedia);
        }
      });
  }

  openMediaToPreview() {
    const mediaToPreview = this._savedMedia() ?? this.newFile();
    this.modalService.openModal<MediaPreviewModalData, void>(MediaPreviewModalComponent, {
      modalTitle: mediaToPreview?.name,
      data: {
        mediaSource: this._savedMedia() ? this._savedMedia()!.url! : this.mediaSrc,
        mediaType: this.mediaType(),
      },
    });
  }

  removeFile() {
    this.mediaDeleted.emit();
  }

  protected readonly SegmentType = SegmentConfigType;
  protected readonly MediaType = MediaType;
}
